import { api, getHeaders } from '@/api';
import { validateResponse } from '@/api/utils';
import type { CodeString } from '@/consts/codes';
import type { MutationOptions } from '@tanstack/react-query';

import { SettingCategory } from './queries';
import { settingOperationResponseSchema, type ApiResponse } from './schema';

const endpointsMap: Record<Exclude<SettingCategory, 'User.Web.GridSaveLayout'>, string> = {
  [SettingCategory.Favorites]: 'favoriteMenuItems',
  [SettingCategory.Recents]: 'recentMenuItems'
};

export const settingsMutationEndpoints = {
  add: async (category: SettingCategory, code: CodeString): Promise<void> => {
    if (category === SettingCategory.GridSaveLayout) {
      throw new Error('GridSaveLayout not supported for add mutation');
    }
    const response = await api.post<ApiResponse<null>>(
      `/api/site/${endpointsMap[category]}`,
      { MenuItemCode: code },
      getHeaders()
    );

    const parsed = validateResponse(settingOperationResponseSchema, response.data);
    if (parsed.Error) throw new Error(parsed.Errors?.[0] ?? `Failed to add ${category}`);
  },

  remove: async (category: SettingCategory, code: CodeString): Promise<void> => {
    if (category === SettingCategory.GridSaveLayout) {
      throw new Error('GridSaveLayout not supported for remove mutation');
    }
    const response = await api.delete<ApiResponse<null>>(
      `/api/site/${endpointsMap[category]}/${encodeURIComponent(code)}`,
      getHeaders()
    );

    const parsed = validateResponse(settingOperationResponseSchema, response.data);
    if (parsed.Error) throw new Error(parsed.Errors?.[0] ?? `Failed to remove ${category}`);
  }
};

export const settingsMutations = {
  add: (category: SettingCategory, code: CodeString): MutationOptions<void> => ({
    mutationKey: ['settings', category, 'add', code],
    mutationFn: () => settingsMutationEndpoints.add(category, code)
  }),
  remove: (category: SettingCategory, code: CodeString): MutationOptions<void> => ({
    mutationKey: ['settings', category, 'remove', code],
    mutationFn: () => settingsMutationEndpoints.remove(category, code)
  })
};
